import { PuxWysiwygHtmlBodyType } from 'Shared/components/PuxWysiwyg/PuxWysiwyg'

export type StructuredDataObject = Record<string, unknown>
export type StructuredDataType = StructuredDataObject | StructuredDataObject[]
export type StructuredDataInput =
  | StructuredDataType
  | PuxWysiwygHtmlBodyType
  | string
  | null
  | undefined

const isStructuredDataObject = (
  value: unknown
): value is StructuredDataObject => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const isHtmlBody = (value: unknown): value is PuxWysiwygHtmlBodyType => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'html' in value &&
    typeof value.html === 'string'
  )
}

const decodeStructuredDataHtml = (value: string): string => {
  return value
    .replace(/<[^>]+>/g, ``)
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&amp;/g, '&')
    .trim()
}

const parseStructuredDataString = (
  value: string
): StructuredDataType | undefined => {
  const candidates = [value.trim(), decodeStructuredDataHtml(value)]

  for (const candidate of candidates) {
    if (!candidate.length) {
      continue
    }

    let parsedValue: unknown = candidate

    for (let index = 0; index < 2 && typeof parsedValue === 'string'; index++) {
      try {
        parsedValue = JSON.parse(parsedValue)
      } catch {
        break
      }
    }

    if (Array.isArray(parsedValue)) {
      const items = parsedValue.filter(isStructuredDataObject)

      if (items.length) {
        return items
      }
    }

    if (isStructuredDataObject(parsedValue)) {
      return parsedValue
    }
  }

  return undefined
}

export const normalizeStructuredData = (
  value: StructuredDataInput
): StructuredDataType | undefined => {
  if (!value) {
    return undefined
  }

  const rawValue = isHtmlBody(value) ? value.html : value

  if (typeof rawValue === 'string') {
    return parseStructuredDataString(rawValue)
  }

  if (Array.isArray(rawValue)) {
    const items = rawValue.filter(isStructuredDataObject)

    return items.length ? items : undefined
  }

  if (isStructuredDataObject(rawValue)) {
    return rawValue
  }

  return undefined
}

export const getStructuredDataScriptContent = (
  value: StructuredDataInput
): string | undefined => {
  const normalizedStructuredData = normalizeStructuredData(value)

  return normalizedStructuredData ? JSON.stringify(normalizedStructuredData) : undefined
}

export const mergeStructuredData = (
  ...values: StructuredDataInput[]
): StructuredDataType | undefined => {
  const mergedStructuredData = values.flatMap((value) => {
    const normalizedValue = normalizeStructuredData(value)

    if (!normalizedValue) {
      return []
    }

    return Array.isArray(normalizedValue) ? normalizedValue : [normalizedValue]
  })

  if (!mergedStructuredData.length) {
    return undefined
  }

  return mergedStructuredData.length === 1 ? mergedStructuredData[0] : mergedStructuredData
}